import { IconButton, IPanelHeaderRenderer, Stack, Text, TextField } from "@fluentui/react";
import { DocumentViewModalState } from "common/components/DocumentList";
import { DocumentViewModal, VIEW_DOC_MODAL_WIDTH } from "common/components/DocumentList/DocumentViewModal";
import DraggablePanel from "common/components/DraggablePanel";
import { PanelHeader } from "common/components/PanelHeader";
import { useCommonStyles } from "common/styles";
import { PanelCommonProps } from "common/types/utility";
import { getWindowDimensions } from "common/utils/columnUtilities";
import { useState } from "react";
import { PersoanlReports_entityDocumentPersonalPools_nodes } from "../ReportList/__generated__/PersoanlReports";
import { useStyles } from "./index.styles";

interface ReportViewProps {
    onDismiss: () => void;
    documentData: PersoanlReports_entityDocumentPersonalPools_nodes;
}

export const ReportView: React.FC<ReportViewProps> = ({
    onDismiss,
    documentData
}) => {
    const styles = useStyles();
    const commonStyles = useCommonStyles();
    const [expandDocumentContent, setExpandDocumentContent] = useState(false);
    const { isImageDescriptionExists, extendedDescription, isAlternateDescriptionExists, isTranslatedDescriptionExists,
        alternateDescription, sourceLanguage, translatedDescription } = { ...documentData?._documentTextExtension }
    const [docViewState, setDocViewState] = useState<DocumentViewModalState>({
        isOpen: false,
        _fileType: 'pdf',
    });
    const _onRenderHeader: IPanelHeaderRenderer = () => {
        const documentTypeLabel = documentData.documentTypes?.documentType
        const viewDocumentDisable =
            (documentData._isProtected !== undefined || documentData._fileViewer !== undefined) &&
            (documentData._isProtected || documentData._fileViewer !== 'browser');
        return (
            <PanelHeader
                hasHeaderText={false}
                onClose={onDismiss}
            >
                <Stack grow horizontal horizontalAlign="space-between">
                    <Stack tokens={{ childrenGap: 10 }}>
                        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                            <Text variant="xLarge">Document</Text>
                            {documentTypeLabel && (
                                <Text variant="xLarge" className={commonStyles.colorThemePrimary}>
                                    {documentTypeLabel}
                                </Text>
                            )}
                        </Stack>
                    </Stack>
                    {documentData?.id && (
                        <Stack horizontal tokens={{ childrenGap: 10 }}>
                            <IconButton
                                disabled={viewDocumentDisable}
                                iconProps={{ iconName: 'View' }}
                                onClick={() => {
                                    const { fileReference, id, _fileType } = { ...documentData };
                                    setDocViewState({
                                        isOpen: true,
                                        title: fileReference,
                                        entityDocumentId: id,
                                        _fileType: _fileType!,
                                    });
                                }}
                            />
                        </Stack>
                    )}
                </Stack>
            </PanelHeader>
        );
    };
    const { width } = getWindowDimensions();

    const documentViewPosition = width
        ? width - (900 + VIEW_DOC_MODAL_WIDTH)
        : 50;

    return (
        <DraggablePanel
            {...PanelCommonProps}

            initialWidth={900}
            minWidth={900}
            isBlocking={false}
            onRenderHeader={_onRenderHeader}
            onRenderFooter={() => (
                <Stack>footer</Stack>
            )}
            isOpen
            onDismiss={onDismiss}
            isLightDismiss
        >
            <Stack
                tokens={{ childrenGap: 20 }}
                style={{ padding: '0px 25px 150px 25px' }}
                styles={{ root: { width: '100%' } }}
            >
                <Stack tokens={{ childrenGap: 20 }}
                    styles={{
                        root: {
                            marginTop: '30px',
                            padding: 30,
                        },
                    }}
                >
                    <TextField
                        label={'Document Type'}
                        value={documentData.documentTypes?.documentType ?? ''}
                    />
                    <TextField
                        label={'Documents'}
                        value={documentData.fileReference ?? ''}
                    />
                    <TextField
                        label={'Contents'}
                        value={documentData._documentContents ?? ''}
                    />
                    <TextField
                        label={'Comments'}
                        value={documentData.comment ?? ''}
                    />
                    {!!documentData?._isDocumentTextExtensionExists &&
                        <Stack tokens={{ childrenGap: 20 }}>
                            <Stack horizontal tokens={{ childrenGap: 10 }} style={{ marginTop: 20 }}>
                                <Text variant="xLarge"  >
                                    Document Content
                                </Text>
                                {!expandDocumentContent ? (
                                    <IconButton
                                        onClick={() => setExpandDocumentContent(true)}
                                        iconProps={{ iconName: 'ChevronDown' }}
                                        className={styles.iconButtonColor}
                                    />
                                ) : (
                                    <IconButton
                                        onClick={() => setExpandDocumentContent(false)}
                                        iconProps={{ iconName: 'ChevronUp' }}
                                        className={styles.iconButtonColor}
                                    />
                                )}
                            </Stack>
                            {expandDocumentContent && <>
                                <TextField
                                    multiline
                                    label={isImageDescriptionExists ? 'Image' : 'Full Text'}
                                    value={extendedDescription ?? ''}
                                    resizable={false}
                                    className={extendedDescription ? styles.textAreaField : undefined}
                                />
                                {isAlternateDescriptionExists && <TextField
                                    multiline
                                    label={'Full Text'}
                                    value={alternateDescription ?? ''}
                                    resizable={false}
                                    className={extendedDescription ? styles.textAreaField : undefined}
                                />}
                                {isTranslatedDescriptionExists && <TextField
                                    multiline
                                    label={sourceLanguage ?? ''}
                                    value={translatedDescription ?? ''}
                                    resizable={false}
                                    className={extendedDescription ? styles.textAreaField : undefined}
                                />}
                            </>}
                        </Stack>
                    }
                </Stack>
                <DocumentViewModal
                    paddingTop={0}
                    paddingLeft={documentViewPosition}
                    onDismiss={() =>
                        setDocViewState({ isOpen: false, _fileType: 'pdf' })
                    }
                    {...docViewState}
                />
            </Stack>
        </DraggablePanel>
    );
}